import { useState } from 'react'
import { motion, AnimateSharedLayout } from 'framer-motion'
import Image from 'components/Image'
import { headers as ease } from 'constants/easings'
import { mobileMenuMaxWidth } from 'constants/styles'

type Props = {
	title: string
	image: string
	questions: Array<{
		question: string
		answer: string
	}>
}

export default function FAQ({ title, image, questions }: Props ) {
	return (
		<section className="container">
			<Image
				src={image}
				loading="lazy"
				sizes="110vh"
			/>
			<div className="text">
				{title}
				<Questions questions={questions} />
			</div>
			<style jsx>{`
				section {
					display: flex;
					justify-content: space-between;
				}
				section :global( img ) {
					width: calc( 100%/12 * 5 );
					height: 110vh;
					max-height: 900px;
					margin-left: calc( 50% - 50vw );
					object-fit: cover;
				}
				h2 {
					margin-bottom: 2.8em;
					line-height: 1.27;
				}
				section :global( .questions ) {
					margin-top: 50px;
				}
				.text {
					flex: 0 0 calc( 100%/12 * 7 );
				}
				.columns {
					display: grid;
					grid-template-columns: 1fr 1fr;
					grid-column-gap: 100px;
				}
				.columns :global( > div ) {
					border-top: solid 1px #000000;
				}
				.columns :global( p ) {
					font-size: 20px;
					line-height: 1.65;
				}
				.columns :global( p:first-child ) {
					margin-top: 1em;
					margin-bottom: 4em;
				}
				@media ( max-width: 700px ) {
					section :global( img ) {
						display: none;
					}
					.text {
						flex: 1;
					}
				}
			`}</style>
		</section>
	)
}

export const Questions = ({ questions }: Pick<Props, 'questions'> ) => (
	<div>
		<AnimateSharedLayout>
			<motion.ul
				animate
				className="questions"
				transition={{ duration: 1, ease }}
			>
				{questions.map( ({ question, answer }, i ) => (
					<Question key={i} {...{ question, answer }} />
				) )}
			</motion.ul>
		</AnimateSharedLayout>
		<style jsx>{`
			div :global( ul ) {
				counter-reset: faqs;
			}
			div :global( li ) {
				counter-increment: faqs;
				position: relative;
				padding-top: 30px;
				padding-bottom: 30px;
				padding-left: 45px;
				border: 1px solid #c6cdd6;
				border-width: 1px 0 0;
				font-size: 20px;
				cursor: pointer;
			}
			div :global( li::before ) {
				content: counter(faqs);
				position: absolute;
				left: 0;
			}
			div :global( li + li ) {
				margin-top: -2px;
			}
			div :global( .answer ) {
				margin-top: 37px;
			}
			@media ( max-width: ${mobileMenuMaxWidth}px ) {
				div :global( li ) {
					font-size: 18px;
				}
			}
		`}</style>
	</div>
)

const Question = ({ question, answer }) => {
	const [ expanded, setExpanded ] = useState( false )
	return (
		<motion.li
			animate={( expanded ? 'expanded' : 'collapsed' )}
			onClick={() => setExpanded( !expanded )}
		>
			<div style={{ display: 'flex', alignItems: 'flex-start' }}>
				<div style={{ flex: 1 }}>{question}</div>
				<motion.svg
					viewBox="0 0 14 8"
					width="14"
					height="8"
					style={{ marginTop: 11 }}
					variants={{
						collapsed: { rotate: '0deg' },
						expanded: { rotate: '180deg' },
					}}
				>
					{/* eslint-disable-next-line max-len */}
					<polygon fill="#B0BBC4" points="12.5856969 0 6.99949989 5.31082848 1.41430306 0 0 1.34458576 6.99949989 8 14 1.34458576" />
				</motion.svg>
			</div>
			<motion.div
				style={{ overflow: 'hidden' }}
				variants={{
					collapsed: { opacity: 0, height: 0 },
					expanded: { opacity: 1, height: 'auto' },
				}}
				transition={{ duration: 1, ease }}
			>
				<div style={{ height: 37 }} />
				{answer}
			</motion.div>
		</motion.li>
	)
}
